import { useMemo, useState } from 'react'
import { Avatar, Popover, TextInput, UnstyledButton } from '@mantine/core'
import { IconSearch, IconUserCircle, IconX } from '@tabler/icons-react'
import { QUEUE_COLOR } from '@/pages/CareCoordination/queueTheme'
import { InitialsAvatar } from './InitialsAvatar'
import {
  useAssignees,
  useAutofocusOnOpen,
  useUpdateItemValue,
  type CellProps,
  type CellRef,
} from '../hooks'

// Monday-style assignee picker: the cell shows only the avatar (or a ghost
// icon when unassigned); clicking opens a popover with the current assignee as
// a removable chip, a search box, and the people list.
export const PersonCell = ({ item, column }: CellProps) => {
  const { update } = useUpdateItemValue(item)
  const { data } = useAssignees()
  const [opened, setOpened] = useState(false)
  const [search, setSearch] = useState('')
  const searchRef = useAutofocusOnOpen<HTMLInputElement>(opened)
  const raw = item.values[column.id]
  const current: CellRef | null = typeof raw === 'object' && raw !== null ? raw : null

  const people = useMemo(() => {
    const list = [...(data?.assignees ?? [])]
    // Keep the stored assignee visible even if the directory response misses it.
    if (current && !list.some((a) => a.reference === current.reference)) list.push(current)
    const query = search.trim().toLowerCase()
    return query ? list.filter((a) => a.display.toLowerCase().includes(query)) : list
  }, [data, current, search])

  const pick = (person: CellRef | null) => {
    void update(column.id, person)
    setOpened(false)
    setSearch('')
  }

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      width={280}
      position="bottom"
      shadow="md"
      withinPortal
      trapFocus={false}
    >
      <Popover.Target>
        <UnstyledButton
          aria-label={current ? `Assignee: ${current.display}` : 'Set assignee'}
          className="flex w-full cursor-pointer items-center justify-start gap-[9px] pr-3 text-left"
          onClick={() => setOpened((prev) => !prev)}
        >
          {/* Avatar + full name in the queue row, same as the patient cell.
              Unassigned says so in words: an icon alone left people unsure
              whether the cell was empty or still loading. */}
          {current ? (
            <>
              <InitialsAvatar name={current.display} tone="person" />
              <span className="truncate text-[13.5px]" style={{ color: '#4a5462' }}>
                {current.display}
              </span>
            </>
          ) : (
            <>
              <IconUserCircle size={22} style={{ color: QUEUE_COLOR.ringIdle }} />
              <span className="text-[13.5px]" style={{ color: QUEUE_COLOR.subtle }}>
                Unassigned
              </span>
            </>
          )}
        </UnstyledButton>
      </Popover.Target>
      <Popover.Dropdown className="p-3">
        <div className="flex flex-col gap-2">
          {current && (
            <div className="flex">
              <span className="flex items-center gap-1.5 rounded-full bg-[#E3EDF7] py-1 pr-1.5 pl-1">
                <Avatar name={current.display} color="#275587" size={20} />
                <span className="text-xs">{current.display}</span>
                <button
                  type="button"
                  aria-label="Remove assignee"
                  onClick={() => pick(null)}
                  className="cursor-pointer text-gray-500 transition hover:text-gray-700"
                >
                  <IconX size={12} />
                </button>
              </span>
            </div>
          )}

          <TextInput
            ref={searchRef}
            size="xs"
            placeholder="Search names…"
            leftSection={<IconSearch size={14} />}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setOpened(false)
            }}
          />

          <p className="text-xs text-gray-500">Suggested people</p>
          <div className="flex max-h-56 flex-col overflow-y-auto">
            {people.map((person) => (
              <button
                key={person.reference}
                type="button"
                onClick={() => pick(person)}
                className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-left transition hover:bg-gray-50"
              >
                <Avatar name={person.display} color="#275587" size={24} />
                <span className="truncate text-sm">{person.display}</span>
              </button>
            ))}
            {people.length === 0 && <p className="px-2 py-2 text-sm text-gray-400">No matches</p>}
          </div>
        </div>
      </Popover.Dropdown>
    </Popover>
  )
}
